import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveMap = ({ 
  selectedCountries, 
  timelinePosition, 
  viralityThreshold, 
  harmThreshold,
  onCountrySelect,
  onTimelineChange 
}) => {
  const mapRef = useRef(null);
  const [mapData, setMapData] = useState(null);
  const [overlayMode, setOverlayMode] = useState('virality');
  const [isPlaying, setIsPlaying] = useState(false);

  // Mock EU countries data with narrative spread metrics
  const narrativeData = [
    {
      country: 'Germany',
      code: 'DE',
      coordinates: [10.4515, 51.1657],
      narratives: [
        {
          id: 'n1',
          theme: 'Energy Crisis Manipulation',
          virality: 85,
          harm: 72,
          timeline: [
            { date: '2024-09-01', intensity: 20 },
            { date: '2024-09-07', intensity: 45 },
            { date: '2024-09-14', intensity: 85 },
            { date: '2024-09-21', intensity: 72 }
          ]
        }
      ]
    },
    {
      country: 'France',
      code: 'FR',
      coordinates: [2.2137, 46.2276],
      narratives: [
        {
          id: 'n2',
          theme: 'Immigration Fearmongering',
          virality: 67,
          harm: 58,
          timeline: [
            { date: '2024-09-01', intensity: 15 },
            { date: '2024-09-07', intensity: 35 },
            { date: '2024-09-14', intensity: 67 },
            { date: '2024-09-21', intensity: 58 }
          ]
        }
      ]
    },
    {
      country: 'Poland',
      code: 'PL',
      coordinates: [19.1343, 51.9194],
      narratives: [
        {
          id: 'n3',
          theme: 'EU Sovereignty Undermining',
          virality: 92,
          harm: 84,
          timeline: [
            { date: '2024-09-01', intensity: 30 },
            { date: '2024-09-07', intensity: 60 },
            { date: '2024-09-14', intensity: 92 },
            { date: '2024-09-21', intensity: 84 }
          ]
        }
      ]
    },
    {
      country: 'Italy',
      code: 'IT',
      coordinates: [12.5674, 41.8719],
      narratives: [
        {
          id: 'n4',
          theme: 'Economic Destabilization',
          virality: 54,
          harm: 41,
          timeline: [
            { date: '2024-09-01', intensity: 10 },
            { date: '2024-09-07', intensity: 25 },
            { date: '2024-09-14', intensity: 54 },
            { date: '2024-09-21', intensity: 41 }
          ]
        }
      ]
    },
    {
      country: 'Spain',
      code: 'ES',
      coordinates: [-3.7492, 40.4637],
      narratives: [
        {
          id: 'n5',
          theme: 'Climate Change Denial',
          virality: 38,
          harm: 29,
          timeline: [
            { date: '2024-09-01', intensity: 8 },
            { date: '2024-09-07', intensity: 18 },
            { date: '2024-09-14', intensity: 38 },
            { date: '2024-09-21', intensity: 29 }
          ]
        }
      ]
    }
  ];

  const overlayModes = [
    { id: 'virality', label: 'Virality Score', icon: 'TrendingUp', color: '#DC2626' },
    { id: 'harm', label: 'Harm Assessment', icon: 'AlertTriangle', color: '#D97706' },
    { id: 'coordination', label: 'Coordination Patterns', icon: 'GitBranch', color: '#1E3A8A' },
    { id: 'timeline', label: 'Timeline Progression', icon: 'Clock', color: '#059669' }
  ];

  useEffect(() => {
    if (!mapRef?.current) return;

    const svg = d3?.select(mapRef?.current);
    svg?.selectAll("*")?.remove();

    const width = 800;
    const height = 500;
    
    svg?.attr("width", width)?.attr("height", height);

    // Create projection for Europe
    const projection = d3?.geoMercator()?.center([10, 54])?.scale(600)?.translate([width / 2, height / 2]);

    const path = d3?.geoPath()?.projection(projection);

    // Add country circles based on narrative data
    narrativeData?.forEach(countryData => {
      const [x, y] = projection(countryData?.coordinates);
      const narrative = countryData?.narratives?.[0];
      
      if (!narrative) return;

      const currentMetric = overlayMode === 'virality' ? narrative?.virality : narrative?.harm;
      const radius = Math.max(8, (currentMetric / 100) * 25);
      const opacity = currentMetric >= (overlayMode === 'virality' ? viralityThreshold : harmThreshold) ? 0.8 : 0.3;

      const circle = svg?.append("circle")?.attr("cx", x)?.attr("cy", y)?.attr("r", radius)?.attr("fill", overlayModes?.find(m => m?.id === overlayMode)?.color || '#DC2626')?.attr("opacity", opacity)?.attr("stroke", "#FFFFFF")?.attr("stroke-width", 2)?.style("cursor", "pointer")?.on("click", () => onCountrySelect(countryData?.code));

      // Add country label
      svg?.append("text")?.attr("x", x)?.attr("y", y - radius - 5)?.attr("text-anchor", "middle")?.attr("font-size", "12px")?.attr("font-weight", "500")?.attr("fill", "#1E293B")?.text(countryData?.country);

      // Add metric value
      svg?.append("text")?.attr("x", x)?.attr("y", y + 4)?.attr("text-anchor", "middle")?.attr("font-size", "10px")?.attr("font-weight", "600")?.attr("fill", "#FFFFFF")?.text(currentMetric);
    });

    // Add coordination lines between countries with high correlation
    const coordinationPairs = [
      ['DE', 'PL'],
      ['FR', 'IT'],
      ['DE', 'FR']
    ];

    if (overlayMode === 'coordination') {
      coordinationPairs?.forEach(([code1, code2]) => {
        const country1 = narrativeData?.find(c => c?.code === code1);
        const country2 = narrativeData?.find(c => c?.code === code2);
        
        if (country1 && country2) {
          const [x1, y1] = projection(country1?.coordinates);
          const [x2, y2] = projection(country2?.coordinates);
          
          svg?.append("line")?.attr("x1", x1)?.attr("y1", y1)?.attr("x2", x2)?.attr("y2", y2)?.attr("stroke", "#1E3A8A")?.attr("stroke-width", 2)?.attr("stroke-dasharray", "5,5")?.attr("opacity", 0.6);
        }
      });
    }

  }, [overlayMode, viralityThreshold, harmThreshold, selectedCountries]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      // Simulate timeline progression
      const interval = setInterval(() => {
        onTimelineChange(prev => {
          const newPos = prev >= 100 ? 0 : prev + 5;
          if (newPos === 0) {
            setIsPlaying(false);
            clearInterval(interval);
          }
          return newPos;
        });
      }, 500);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft overflow-hidden">
      {/* Map Controls */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-foreground">EU Narrative Spread Map</h3>
          <div className="flex items-center space-x-2">
            {overlayModes?.map((mode) => (
              <Button
                key={mode?.id}
                variant={overlayMode === mode?.id ? "default" : "outline"}
                size="sm"
                iconName={mode?.icon}
                iconPosition="left"
                onClick={() => setOverlayMode(mode?.id)}
              >
                {mode?.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Timeline Controls */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              iconName={isPlaying ? "Pause" : "Play"}
              onClick={handlePlayPause}
            />
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Timeline:</span>
              <input
                type="range"
                min="0"
                max="100"
                value={timelinePosition}
                onChange={(e) => onTimelineChange(parseInt(e?.target?.value))}
                className="w-24 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm font-medium text-foreground">{timelinePosition}%</span>
            </div>
          </div>

          {/* Export Options */}
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>
      {/* Interactive Map */}
      <div className="relative bg-slate-50 min-h-[500px] flex items-center justify-center">
        <svg ref={mapRef} className="w-full h-full" />
        
        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-sm rounded-lg border border-border p-3 shadow-soft">
          <h4 className="text-sm font-semibold text-foreground mb-2">
            {overlayModes?.find(m => m?.id === overlayMode)?.label} Legend
          </h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-red-600 opacity-80"></div>
              <span className="text-xs text-muted-foreground">High Impact (70-100)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-600 opacity-50"></div>
              <span className="text-xs text-muted-foreground">Medium Impact (40-69)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-red-600 opacity-30"></div>
              <span className="text-xs text-muted-foreground">Low Impact (0-39)</span>
            </div>
          </div>
        </div>

        {/* Real-time Status */}
        <div className="absolute top-4 right-4 bg-card/95 backdrop-blur-sm rounded-lg border border-border p-3 shadow-soft">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-foreground">Live Monitoring</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Last updated: {new Date()?.toLocaleTimeString()}
          </div>
        </div>
      </div>
      {/* Threshold Controls */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Virality Threshold:</span>
            <input
              type="range"
              min="0"
              max="100"
              value={viralityThreshold}
              onChange={(e) => onTimelineChange(parseInt(e?.target?.value))}
              className="w-20 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-sm font-medium text-foreground w-8">{viralityThreshold}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Harm Threshold:</span>
            <input
              type="range"
              min="0"
              max="100"
              value={harmThreshold}
              onChange={(e) => onTimelineChange(parseInt(e?.target?.value))}
              className="w-20 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-sm font-medium text-foreground w-8">{harmThreshold}</span>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          Showing {narrativeData?.length} countries • {narrativeData?.reduce((acc, c) => acc + c?.narratives?.length, 0)} active narratives
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;